import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Tag } from 'lucide-react';
import dayjs from 'dayjs';
import { useRef } from 'react';
import { PostSummary } from '../types';

interface PostCardProps {
  post: PostSummary;
  featured?: boolean;
}

export function PostCard({ post, featured }: PostCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    cardRef.current.style.setProperty('--mouse-x', `${x}%`);
    cardRef.current.style.setProperty('--mouse-y', `${y}%`);
  };

  return (
    <motion.article
      ref={cardRef}
      onMouseMove={handleMouseMove}
      whileHover={{ y: -4 }}
      className={`card-glow glow-border group p-6 transition-all hover:shadow-lg dark:shadow-none ${
        featured ? 'md:col-span-2 md:grid md:grid-cols-2 md:gap-8 md:p-8' : ''
      }`}
    >
      <div className="flex flex-col justify-between">
        <div>
          <div className="mb-3 flex flex-wrap gap-2">
            {post.tags.slice(0, 3).map((tag) => (
              <Link
                key={tag}
                to={`/?tag=${encodeURIComponent(tag)}`}
                className="tag-pill bg-indigo-50 text-indigo-600 transition-colors hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
              >
                <Tag size={10} />
                {tag}
              </Link>
            ))}
          </div>
          <Link to={`/posts/${post.slug}`}>
            <h3 className="mb-3 font-serif text-xl font-bold text-stone-900 transition-colors group-hover:text-indigo-600 dark:text-star dark:group-hover:text-nebula-glow md:text-2xl">
              {post.title}
            </h3>
          </Link>
          {post.summary && (
            <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-stone-600 dark:text-star-dim">
              {post.summary}
            </p>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-stone-500 dark:text-star-dim">
          <Calendar size={12} />
          <time dateTime={post.published_at}>
            {post.published_at ? dayjs(post.published_at).format('YYYY-MM-DD') : '未发布'}
          </time>
        </div>
      </div>
      {featured && (
        <div className="mt-6 hidden overflow-hidden rounded-xl md:mt-0 md:block">
          {post.cover_url ? (
            <img
              src={post.cover_url}
              alt={post.title}
              className="h-full max-h-72 w-full rounded-xl object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 dark:from-indigo-500/20 dark:to-purple-500/20">
              <div className="text-6xl font-black text-indigo-300/30 dark:text-indigo-400/20">Blog</div>
            </div>
          )}
        </div>
      )}
    </motion.article>
  );
}
